import { format } from 'date-fns';

/**
 * Calculates the number of full months between two dates.
 * @param dateFrom start date
 * @param dateTo end date
 * @returns number of full months between dateFrom and dateTo
 */
export const fullMonthDiff = (dateFrom: Date | undefined, dateTo: Date | undefined): number => {
  let monthDifference = 0;

  if (dateFrom && dateTo) {
    monthDifference =
      dateTo.getMonth() -
      dateFrom.getMonth() +
      12 * (dateTo.getFullYear() - dateFrom.getFullYear());
    if (dateFrom.getDate() > dateTo.getDate()) {
      monthDifference--;
    }
    if (monthDifference < 0) {
      monthDifference = 0;
    }
  }
  return monthDifference;
};

/**
 * Returns the newer of two dates, or undefined if both are undefined.
 * @param date1 first date
 * @param date2 second date
 * @returns the newer date or undefined
 */
export function getNewerDate(date1: Date | undefined, date2: Date | undefined): Date | undefined {
  if (date1 && date2) {
    if (date1 > date2) {
      return date1;
    }
    return date2;
  }
  if (date1) {
    return date1;
  }
  return date2;
}

/**
 * Formats a date to 'd. M. yyyy' format.
 * @param date date to format
 * @returns formatted date string
 */
export function formatDate(date: Date | string): string {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  return format(date, 'd. M. yyyy');
}

/**
 * Formats a date to 'd. M. yyyy HH:mm' format.
 * @param date date to format
 * @returns formatted date-time string
 */
export function formatDateTime(date: Date | string): string {
  if (typeof date === 'string') {
    date = new Date(date);
  }
  return format(date, 'd. M. yyyy HH:mm');
}

/**
 * Gets the current date formatted as 'yyyyMMdd'.
 * @returns current date string in 'yyyyMMdd' format
 */
export function getCurrentDateYYYYMMDD(): string {
  const date: Date = new Date();
  return format(date, 'yyyyMMdd');
}

/**
 * A date as a few characters: age when recent, day and month past a week. Returns '' for an
 * unreadable date - a list label is not worth throwing over. `now` is injectable for tests.
 */
export function formatRelativeShort(date: Date | string, now: Date = new Date()): string {
  const then = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(then.getTime())) {
    return '';
  }

  // Clamped: a clock a little ahead of the server should read as 'now', not as a negative age.
  const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));

  if (seconds < 60) {
    return 'now';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d`;
  }

  // The year only earns its place once it is not this one.
  return then.getFullYear() === now.getFullYear()
    ? format(then, 'd. M.')
    : format(then, 'd. M. yyyy');
}
